import React, { useState, useRef, useEffect } from 'react';
import { Filter, Plus } from 'lucide-react';
import { FilterBuilderProps, FilterCondition } from './types';
import { FilterConditionRow } from './FilterConditionRow';

export const FilterBuilder: React.FC<FilterBuilderProps> = ({
  availableFilters,
  conditions,
  onApply,
  buttonText = 'Filter',
  buttonClassName,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [localConditions, setLocalConditions] = useState<FilterCondition[]>(conditions);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Sync local conditions with prop changes
  useEffect(() => {
    setLocalConditions(conditions);
  }, [conditions]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        buttonRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleAddCondition = () => {
    const firstFilter = availableFilters[0];
    if (!firstFilter) return;

    const newCondition: FilterCondition = {
      id: `filter-${Date.now()}`,
      field: firstFilter.field,
      operator: firstFilter.operators[0]?.value || '=',
      value: firstFilter.valueType === 'boolean' ? true : '',
    };

    setLocalConditions([...localConditions, newCondition]);
  };

  const handleUpdateCondition = (index: number, updatedCondition: FilterCondition) => {
    const newConditions = [...localConditions];
    newConditions[index] = updatedCondition;
    setLocalConditions(newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = localConditions.filter((_, i) => i !== index);
    setLocalConditions(newConditions);
  };

  const handleClearAll = () => {
    setLocalConditions([]);
  };

  const handleApply = () => {
    onApply(localConditions);
    setIsOpen(false);
  };

  const activeFilterCount = conditions.length;

  const defaultButtonClass = "inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 border-gray-300 hover:bg-gray-50";

  return (
    <div className="relative">
      {/* Filter Button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={buttonClassName || defaultButtonClass}
      >
        <Filter className="h-3.5 w-3.5 mr-1.5" />
        {buttonText}
        {activeFilterCount > 0 && (
          <span className="ml-1.5 px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">
            {activeFilterCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute top-full mt-2 right-0 z-50 w-[560px] bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          <div className="p-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Filter Conditions</h3>
              <button
                type="button"
                onClick={handleAddCondition}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add Filter
              </button>
            </div>

            {/* Conditions */}
            <div className="space-y-2 mb-4">
              {localConditions.length === 0 ? (
                <div className="text-xs text-gray-500 text-center py-4">
                  No filters applied. Click "Add Filter" to get started.
                </div>
              ) : (
                localConditions.map((condition, index) => (
                  <FilterConditionRow
                    key={condition.id}
                    condition={condition}
                    availableFilters={availableFilters}
                    onUpdate={(updated) => handleUpdateCondition(index, updated)}
                    onRemove={() => handleRemoveCondition(index)}
                    showRemove={localConditions.length > 1}
                  />
                ))
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClearAll}
                disabled={localConditions.length === 0}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              >
                Clear All
              </button>
              <button
                type="button"
                onClick={handleApply}
                className="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded-lg transition-colors shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-avi-primary text-white border-avi-primary hover:bg-blue-600"
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
